/**
 * The two sums the donor surfaces draw from a goal projection, written once (GH-781).
 *
 * ## Why this is a module and not three copies
 *
 * The headline rule — *received, plus active pledges when the event counts them* — had been
 * written out three times by the time the event page needed a fourth: `live-display`'s
 * `Display.tsx`, `OrgLanding.tsx`'s private `headline()` (whose own comment reads "Verbatim from
 * live-display"), and iOS `GoalResponse.headline`. Three copies of an arithmetic rule about money
 * is three chances for one of them to answer a different night, and a donor looking from this
 * page to the projector at the back of the hall would see two different totals with no way to
 * know which is the fundraiser.
 *
 * The web copies collapse into this file. iOS keeps its own because Swift cannot import it, and
 * `packages/contracts` is the wrong home: the fold is a *presentation* choice about which figure
 * a room celebrates, not part of the wire.
 *
 * ## The server owns every number here
 *
 * Nothing in this file invents money. It adds two amounts the server sent and divides by a third,
 * and the flag deciding whether to add at all is also the server's — `countsPledges` is an
 * operator switch, rendered rather than assumed. `CLAUDE.md`: never trust a client-calculated
 * amount. What a client may do is arrange what it was given, which is all of this.
 */
import type { GoalResponse } from "@givehere/contracts";

/** Minor units, as `bigint`. Every amount on the wire is a decimal string for exactly this. */
const minor = (amount: { readonly amountMinor: string }): bigint => BigInt(amount.amountMinor);

/**
 * Whether promises belong in the headline at all.
 *
 * One predicate rather than the flag read inline, because "the operator switched pledges into the
 * total" and "somebody has pledged" are different questions and the bar has to ask both: an event
 * whose operator left the switch off must show no promise anywhere, and an event with the switch
 * on and nothing pledged must not draw an empty striped segment against the track.
 */
export const countsPledges = (goal: GoalResponse): boolean =>
  goal.countsPledges && minor(goal.pledged) > 0n;

/**
 * The figure the room watches: received, plus active pledges when the event counts them.
 *
 * Folding is only ever safe while the parts stay visible underneath — every caller of this
 * function also renders "Received … · Pledged …", and a screen that lost that line would be
 * claiming money had arrived that has only been promised (`CLAUDE.md`, ADR 0012).
 */
export function headline(goal: GoalResponse): { amountMinor: string; currency: "USD" } {
  const total = minor(goal.received) + (goal.countsPledges ? minor(goal.pledged) : 0n);
  return { amountMinor: total.toString(10), currency: goal.received.currency };
}

/**
 * How much of the track each part fills, clamped **as a pair**.
 *
 * Two rules, and both of them are bugs somebody has already shipped in one form or another:
 *
 *  - **Scale before dividing.** `(part * 1000n) / target` and then a divide by ten in `Number`
 *    space. A bigint quotient floors, so the naive `(part / target) * 100` reports 99.4% as 99%
 *    and — the case that matters at the start of an evening — reports every amount under 1% of
 *    the goal as exactly zero. `Display.tsx` carries the same trick with the same comment.
 *  - **Clamp the two together.** Received 90% and pledged 30% must render 90 and 10, not 90 and
 *    30. Clamping each to 100 on its own paints the pledged block past the end of the track,
 *    which is the horizontal equivalent of the overflow the display's `clipPath` exists to stop.
 *
 * A zero or absent goal yields zero for both. The caller draws no bar at all in that case — a
 * track that looks empty and a track that looks full are both lies about a target nobody set —
 * but returning zeroes rather than throwing keeps that a rendering decision rather than a crash.
 */
export function goalPercents(goal: GoalResponse): {
  readonly received: number;
  readonly pledged: number;
} {
  const target = minor(goal.goal);
  if (target <= 0n) return { received: 0, pledged: 0 };

  const share = (amount: bigint): number => Number((amount * 1000n) / target) / 10;
  const received = Math.min(100, share(minor(goal.received)));
  const pledged = countsPledges(goal) ? Math.min(100 - received, share(minor(goal.pledged))) : 0;
  return { received, pledged };
}
